/**
 * PUNTO ÚNICO DE CONFIGURACIÓN DEL API DE CONTENIDOS (noticias y slides).
 *
 * Cuando el compañero que está haciendo el API pase las rutas definitivas,
 * ESTE es el único fichero que hay que tocar: se cambian las cadenas de abajo
 * y el resto de la aplicación sigue funcionando sin más cambios.
 *
 * Mientras `VITE_API_BASE_URL` esté vacía, `isApiEnabled()` devuelve `false` y
 * los servicios siguen tirando del almacén en memoria de siempre. Basta con
 * rellenar esa variable en `.env.local` para que la web empiece a hablar con el
 * API de verdad, sin tocar una línea de código.
 */

/** URL base del API, sin barra final. Ej.: `https://api.solimat.com/v1`. */
const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/+$/, '');

/**
 * ¿Hay API configurada?
 *
 * Es lo que decide, en tiempo de ejecución, si los servicios llaman por HTTP o
 * siguen con el almacén en memoria. No hay ningún otro interruptor.
 */
export const isApiEnabled = (): boolean => BASE_URL !== '';

/** Convierte una ruta relativa del API en la URL absoluta a la que se llama. */
export const resolveUrl = (path: string): string =>
  `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

/**
 * Rutas de noticias del backend real (ASP.NET). SOLO LECTURA a propósito: esta
 * web no da de alta, edita, publica ni borra noticias — eso vive en el CMS
 * aparte (Web.CMS.React), que habla con el mismo backend. No añadir aquí una
 * ruta de POST/PUT/DELETE sin hablarlo antes.
 *
 * El prefijo `/api/Noticias` va incluido aquí: `VITE_API_BASE_URL` es solo el
 * host (p. ej. `https://api.solimat.com`), sin `/api`.
 */
export const NOTICIAS_ENDPOINTS = {
  /** GET → detalle por slug. `200 NoticiaResponse | 404`. */
  detailPorSlug: (slug: string) => `/api/Noticias/${encodeURIComponent(slug)}`,
  /** GET → solo las publicadas, para el listado público. Devuelve todas de una vez. */
  searchPublicadas: '/api/Noticias/search-publicadas',
} as const;

/** Rutas de los slides del carrusel del home. */
export const SLIDES_ENDPOINTS = {
  /** GET  → listado completo (admin), borradores incluidos. */
  list: '/slides',
  /** GET  → solo los publicados, ya ordenados (web pública). */
  published: '/slides/publicados',
  /** GET  → detalle. */
  detail: (id: string) => `/slides/${encodeURIComponent(id)}`,
  /** POST → alta. Nace en estado `draft`. */
  create: '/slides',
  /** PUT  → edición completa. */
  update: (id: string) => `/slides/${encodeURIComponent(id)}`,
  /** DELETE → baja. */
  remove: (id: string) => `/slides/${encodeURIComponent(id)}`,
  /** POST → publicar: pasa de `draft` a `published`. */
  publish: (id: string) => `/slides/${encodeURIComponent(id)}/publicar`,
  /** PATCH → mover a una posición concreta del carrusel. */
  reorder: (id: string) => `/slides/${encodeURIComponent(id)}/orden`,
} as const;
